import { Injectable, Injector } from '@angular/core';
import { formatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

import { LoggingService } from '../../logging/logic/logging.service';
import { PushNotificationsService } from '../../push-notifications/logic/push-notifications.service';
import { DynFormsService } from '../../dyn-forms/logic/dyn-forms.service';
import { ServicejobsService } from '../../servicejobs/logic/servicejobs.service';
import { EmployeeService } from '../../employee/logic/employee.service';
import { ActivityConfirmation } from 'src/app/_model/activity-confirmation';

/**
 * Parameters the backend sends with the draft pushes (new_draft_available,
 * draft_revoked, draft_finalized).
 *
 * EVERY field is optional: params with a null value are dropped from the JSON
 * entirely, so a missing key means "unknown", never "zero".
 */
export interface DraftPushParams {
  /**
   * KRK-45: THE key for matching a local row. Identifies the AC version chain and never
   * changes, while every save creates a new acid - so the pushed `objid` is usually newer
   * than anything this device has stored and matches nothing.
   */
  chainrootacid?: number | string;
  /**
   * Was THE matching key before KRK-45 and is kept as a fallback for rows/devices from
   * that time. Not actually stable: a smart renumbering (LBW-251) rewrites it.
   *
   * Set as soon as the document has a number range key.
   */
  acukey?: string;
  /** Id of the ActivityConfirmation version the push is about - extra info only */
  objid?: number | string;
  /** The object the document hangs on - NOT necessarily a service job */
  ownerid?: number | string;
  ownerobjref?: string;
  dynformid?: number | string;
  /**
   * Set when the draft is parked on a service job instead of being held by a person -
   * everybody who has that job may take it over. Comes with the push so the wording is
   * right even when the sync afterwards fails.
   */
  draftownerservicejobid?: number | string;
  /**
   * The push's silent flag, handed down from `#imsapp.slnt` by the action interpreter.
   * `true` means: sync, but do not tell the user - it is their own draft arriving on
   * their own second device, not a handover. Arrives as the string 'true'/'false'.
   */
  slnt?: boolean | string;
}

/**
 * Builds and sends the local notification for a draft push.
 *
 * Shared because the push itself only carries ids - form name, job label and the
 * person are local lookups, and the wording follows the same shape for every draft
 * code. Each caller passes its own i18n prefix and gets the most detailed variant
 * the available data supports:
 *
 *   TITLE / TITLE-FORM                      - with the form name when resolvable
 *   BODY / BODY-JOB / BODY-DETAILS / BODY-DETAILS-JOB
 *
 * Deliberately never renders a sentence with holes: a variant is only used when all
 * of its placeholders can be filled.
 */
@Injectable({
  providedIn: 'root',
})
export class DraftPushNotifier {
  // Lifecycle
  constructor(
    private logger: LoggingService,
    private translate: TranslateService,
    private injector: Injector,
  ) {}

  // Public Functions
  /**
   * Shows the notification unless the push asked for silence. The caller runs its sync
   * either way - the push is what triggers it, and the handoff depends on that even when
   * nothing is displayed.
   */
  public async notify(
    i18nPrefix: string,
    params: DraftPushParams | undefined,
    activityConfirmation: ActivityConfirmation | undefined,
  ): Promise<void> {
    if (this.isSilent(params)) {
      this.logger.log(
        this.constructor.name,
        'Silent draft push',
        i18nPrefix,
        '- synced without notifying',
      );
      return;
    }

    const title = this.buildTitle(i18nPrefix, params, activityConfirmation);
    const body = this.buildBody(i18nPrefix, params, activityConfirmation);

    this.logger.log(
      this.constructor.name,
      'Sending draft notification',
      i18nPrefix,
      '- AC resolved:',
      activityConfirmation != undefined,
      '|',
      title,
      '|',
      body,
    );

    await this.injector
      .get(PushNotificationsService)
      .sendLocalNotification(title, body);
  }

  /** The AC id the push refers to, or undefined when it was not sent */
  public getAcId(params: DraftPushParams | undefined): number | undefined {
    return this.toNumber(params?.objid);
  }

  /** Whether the backend asked for this push to be handled without bothering the user */
  public isSilent(params: DraftPushParams | undefined): boolean {
    const slnt = params?.slnt;
    return slnt === true || slnt === 'true';
  }

  /**
   * Whether the draft is parked on a service job rather than held by a person. Read from
   * the AC when the sync delivered it, from the push params otherwise.
   */
  public isServiceJobDraft(
    params: DraftPushParams | undefined,
    activityConfirmation: ActivityConfirmation | undefined,
  ): boolean {
    return (
      activityConfirmation?.draftOwnerServiceJobId != undefined ||
      this.toNumber(params?.draftownerservicejobid) != undefined
    );
  }

  /** The chain identity the push refers to, or undefined when it was not sent */
  public getAcukey(params: DraftPushParams | undefined): string | undefined {
    const acukey = params?.acukey;
    return acukey == undefined || acukey === '' ? undefined : acukey;
  }

  /** KRK-45: the stable chain identity of the pushed document, see DraftPushParams */
  public getChainRootAcId(params: DraftPushParams | undefined): number | undefined {
    return this.toNumber(params?.chainrootacid);
  }

  // Private Functions
  private buildTitle(
    i18nPrefix: string,
    params: DraftPushParams | undefined,
    activityConfirmation: ActivityConfirmation | undefined,
  ): string {
    const formId = this.toNumber(activityConfirmation?.dynFormId ?? params?.dynformid);
    const formName = this.resolveFormName(formId);
    if (formName == undefined) {
      return this.translate.instant(`${i18nPrefix}.TITLE`);
    }

    const numberRange = activityConfirmation?.numberRangeValue;
    return this.translate.instant(`${i18nPrefix}.TITLE-FORM`, {
      form: numberRange ? `${formName} ${numberRange}` : formName,
    });
  }

  private buildBody(
    i18nPrefix: string,
    params: DraftPushParams | undefined,
    activityConfirmation: ActivityConfirmation | undefined,
  ): string {
    // The job comes from the push params, so it survives even when the AC itself is
    // not (or no longer) part of the sync result - for draft_finalized that is the
    // normal case, and naming the job is then the only useful information left.
    const job = this.resolveJobLabel(
      this.toNumber(activityConfirmation?.ownerId ?? params?.ownerid),
      activityConfirmation?.ownerObjRef ?? params?.ownerobjref,
    );

    const owner = this.resolveOwnerName(activityConfirmation);
    const date = activityConfirmation?.entityCreatedTstamp
      ? formatDate(
          activityConfirmation.entityCreatedTstamp,
          'dd.MM.yyyy HH:mm',
          this.translate.currentLang || 'de',
        )
      : undefined;
    const device = activityConfirmation?.createdDeviceLabel;
    const hasDetails = owner != undefined && date != undefined && device != undefined;

    let key: string;
    if (hasDetails) {
      key = job ? 'BODY-DETAILS-JOB' : 'BODY-DETAILS';
    } else {
      key = job ? 'BODY-JOB' : 'BODY';
    }
    return this.translate.instant(`${i18nPrefix}.${key}`, {
      owner,
      date,
      device,
      job,
    });
  }

  private resolveFormName(formId: number | undefined): string | undefined {
    if (formId == undefined) {
      return undefined;
    }
    return this.injector.get(DynFormsService).getDynFormById({
      imsId: formId,
      secondaryId: undefined,
      objRef: 'ims/DynForm',
    })?.name;
  }

  /**
   * Prefers the employee record for a properly formatted name and falls back to the
   * AC's create user, which is the raw login ("janb") but always present.
   */
  private resolveOwnerName(
    activityConfirmation: ActivityConfirmation | undefined,
  ): string | undefined {
    const empId =
      activityConfirmation?.createdEmpId ?? activityConfirmation?.draftOwnerEmpId;
    const employee = empId
      ? this.injector.get(EmployeeService).getEmployeeById({
          imsId: empId,
          secondaryId: undefined,
          objRef: 'ims/Employee',
        })
      : undefined;
    return employee?.displayLabel ?? activityConfirmation?.createUser;
  }

  /**
   * Only service jobs can be resolved to a label here. `ownerobjref` must therefore
   * be checked rather than assumed - a document can hang on other object types, and
   * looking those up as a service job would silently return the wrong label.
   */
  private resolveJobLabel(
    ownerId: number | undefined,
    ownerObjRef: string | undefined,
  ): string | undefined {
    if (ownerId == undefined || ownerObjRef !== 'ims/ServiceJob') {
      return undefined;
    }
    return this.injector.get(ServicejobsService).getServiceJobById({
      imsId: ownerId,
      secondaryId: undefined,
      objRef: ownerObjRef,
    })?.displayLabel;
  }

  /** Push params arrive as numbers or strings depending on the sender */
  private toNumber(value: number | string | undefined): number | undefined {
    if (value == undefined || value === '') {
      return undefined;
    }
    const parsed = +value;
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}
